(ns ductile.ui.components.columns
  (:require [cljs.reader :as reader]
            [clojure.set :as set]
            [clojure.string :as str]
            [ductile.ui.components :as ui]
            [ductile.ui.components.popover :as popover]
            [ductile.ui.components.rect-selection :as rect-selection]
            [ductile.ui.components.sortable :as sortable]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.locale :refer [tr]]
            [ductile.ui.scene :as scene]
            [nextjournal.clerk.render.hooks :as hooks])
  (:require-macros [ductile.ui.scene.macros :refer [defscene]]))

(defn label [attr]
  (if (vector? attr)
    (str (tr (first attr)) " – " (tr (second attr)))
    (tr attr)))

(defn expand-col
  "Each column is a map with an :attr, to allow saving filters and sorting preferences (future)"
  [c]
  (cond
    (keyword? c) [{:attr c}]
    (map? c) [(dissoc c :label)]
    (vector? c) (into []
                      (map (fn [child] {:attr [(first c) child]}))
                      (if (vector? (second c)) (second c) [(second c)]))))

(defn from-localstorage
  "Read old saved col settings into new profile format"
  [saved-col-settings]
  (let [saved-col-settings (cond->> saved-col-settings
                             (sequential? saved-col-settings) (mapcat expand-col))]
    (if (contains? saved-col-settings ::profiles)
      saved-col-settings
      {::profiles {:default-profile {:cols saved-col-settings}}})))

(defn update-profile [col-settings profile-id f & args]
  (apply update-in col-settings [::profiles profile-id] f args))

(defn update-col [cols attr k f]
  (map (fn [col] (if (= attr (:attr col))
                   (assoc col k (f (k col)))
                   col)) cols))

(defn insert-at [xs idx ys]
  (let [idx (max 0 (min idx (count xs)))]
    (into (vec (take idx xs))
          (concat ys (drop idx xs)))))

(defn move-selected-block [items moving-attrs slot]
  (let [moving-attrs (set moving-attrs)
        moving (vec (filter #(contains? moving-attrs (:attr %)) items))
        remaining (vec (remove #(contains? moving-attrs (:attr %)) items))
        before-slot (count (filter #(contains? moving-attrs (:attr %))
                                   (take slot items)))
        target-idx (max 0 (min (count remaining) (- slot before-slot)))]
    (if (seq moving)
      (insert-at remaining target-idx moving)
      (vec items))))

(defn move-selected-to-edge [items moving-attrs edge]
  (let [moving-attrs (set moving-attrs)
        moving (vec (filter #(contains? moving-attrs (:attr %)) items))
        others (vec (remove #(contains? moving-attrs (:attr %)) items))]
    (case edge
      :top (vec (concat moving others))
      :bottom (vec (concat others moving))
      (vec items))))

(defn contiguous-selection-shape [ordered-attrs selected-attrs attr]
  (if-not (contains? selected-attrs attr)
    :none
    (let [idx (.indexOf ordered-attrs attr)
          prev-attr (when (pos? idx) (nth ordered-attrs (dec idx) nil))
          next-attr (when (and (<= 0 idx) (< idx (dec (count ordered-attrs))))
                      (nth ordered-attrs (inc idx) nil))
          prev-selected? (boolean (and prev-attr (contains? selected-attrs prev-attr)))
          next-selected? (boolean (and next-attr (contains? selected-attrs next-attr)))]
      (cond
        (and (not prev-selected?) (not next-selected?)) :single
        (and (not prev-selected?) next-selected?) :top
        (and prev-selected? next-selected?) :middle
        :else :bottom))))

(defn selection-shape->radius-class [shape]
  (case shape
    :single "rounded"
    :top "rounded-t"
    :middle "rounded-none"
    :bottom "rounded-b"
    :none "rounded"))

(defn selected-profile [{::keys [profiles selected-profile-id]}]
  (let [selected-profile-id (if (contains? profiles selected-profile-id)
                              selected-profile-id
                              :default-profile)
        profile (get profiles selected-profile-id)]
    (assoc profile
           :cols (sort-by (complement :sticky?) (:cols profile))
           :profile-id selected-profile-id
           :label (get-in profiles [selected-profile-id :label] (tr :default-profile)))))

(defn ->table-opts [{:keys [col-settings available-cols default-selected-cols]}]
  (let [col-settings (from-localstorage (or col-settings default-selected-cols))
        {:keys [cols]} (selected-profile col-settings)
        all-attrs (set (map :attr (mapcat expand-col available-cols)))
        selected-attrs (map :attr cols)
        unselected-attrs (set/difference all-attrs (set selected-attrs))
        ->t (fn [attr] (if (vector? attr) (update attr 1 vector) attr))]
    {:schema (map ->t selected-attrs)
     :column-order (map ->t selected-attrs)
     :select-columns (map ->t selected-attrs)
     :hide-columns (map ->t unselected-attrs)
     :sticky-cols-count (count (take-while :sticky? cols))}))

(defn all-profiles [{::keys [profiles]}]
  (if (seq profiles)
    (->> (map (fn [[profile-id {:keys [label]}]]
                {:ui/value profile-id
                 :ui/label (or label (tr :default-profile))})
              profiles)
         (sort-by :ui/label)
         vec)
    [{:ui/value :default-profile
      :ui/label (tr :default-profile)}]))

(defn prompt [text default-value]
  (let [v (js/prompt text default-value)]
    (when (seq (str/trim (str v)))
      (str/trim (str v)))))

(defn create-profile [col-settings]
  (let [new-id (random-uuid)]
    (when-let [new-label (prompt
                          (str (tr "Create profile") ":")
                          (str (tr "Untitled")))]
      (-> col-settings
          (assoc-in [::profiles new-id] {:label new-label})
          (assoc ::selected-profile-id new-id)))))

(defn profile-select [{:keys [col-settings on-change]}]
  (let [{:as selected :keys [label profile-id]} (selected-profile col-settings)]
    [:div.flex.items-center.gap-2
     [ui/select
      {:size 2
       :data-testid "profile-select"
       :value (:profile-id (selected-profile col-settings))
       :on-change
       (fn [new-selected-profile-id]
         (let [new-id (random-uuid)]
           (case new-selected-profile-id
             :new (when-let [new-settings (create-profile col-settings)]
                    (on-change new-settings))
             :duplicate (when-let [new-label (prompt
                                              (str (tr "Duplicate \"%1\" as" label) ":")
                                              (str label " 2"))]
                          (-> col-settings
                              (assoc-in [::profiles new-id]
                                        (-> selected (dissoc :profile-id)
                                            (assoc :label new-label)))
                              (assoc ::selected-profile-id new-id)
                              on-change))
             :rename (when-let [new-label (prompt (str (tr "Rename \"%1\"" label) ":")
                                                  label)]
                       (-> col-settings
                           (assoc-in [::profiles profile-id :label] new-label)
                           on-change))

             :delete (when (js/confirm (tr "Delete \"%1\"?" label))
                       (on-change
                        (let [remaining (update col-settings ::profiles dissoc profile-id)]
                          (if (seq (::profiles remaining))
                            (assoc remaining ::selected-profile-id (ffirst (::profiles remaining)))
                            (assoc remaining ::profiles {:default-profile {}}
                                   ::selected-profile-id :default-profile)))))

             (on-change (assoc col-settings
                               ::selected-profile-id new-selected-profile-id)))))
       :choices (concat (all-profiles col-settings)
                        [{:ui/value nil :ui/label "───────────────────" :disabled true}
                         {:ui/value :new :ui/label (tr "New profile")}
                         {:ui/value :duplicate :ui/label (tr "Duplicate profile")}
                         {:ui/value :rename :ui/label (tr "Rename profile")}
                         {:ui/value :delete :ui/label (tr "Delete profile")}])}]
     [ui/button
      {:variant :white
       :size 2
       :data-testid "new-profile-button"
       :title (tr "New profile")
       :on-click #(when-let [new-settings (create-profile col-settings)]
                    (on-change new-settings))}
      [ui/icon {:size 16} :plus]]]))

(defn select-profile-dropdown [{:keys [col-settings on-change on-modal-open text-class]
                                :or {text-class "text-xs"}}]
  (let [selected-value (::selected-profile-id col-settings :default-profile)
        all (all-profiles col-settings)
        items (concat
               (map (fn [{:ui/keys [value label]}]
                      {:key value
                       :label label
                       :on-select #(on-change (assoc col-settings ::selected-profile-id value))
                       :data-testid (str "select-profile-dropdown-item-" (str value))})
                    all)
               [{:separator? true}
                {:key :customize-columns
                 :label (str (tr "Customize columns") "…")
                 :data-testid "select-profile-dropdown-customize-columns"
                 :on-select on-modal-open}])
        selected-label (or (:ui/label (first (filter #(= (:ui/value %) selected-value) all)))
                           (tr :default-profile))]
    [popover/menu-select
     {:class (str "ml-2 flex shrink-0 items-center gap-1 cursor-pointer text-gray-600 hover:text-gray-700 active:text-gray-800 relative whitespace-nowrap " text-class)
      :button-class "pl-4 whitespace-nowrap"
      :text-class (str text-class " whitespace-nowrap")
      :full-width? false
      :data-testid "select-profile-dropdown"
      :icon :table-columns
      :label selected-label
      :items items}]))

(defn select-columns [{:keys [col-settings on-change on-close
                              available-cols default-selected-cols enable-sticky-cols?]
                       :or {enable-sticky-cols? true}}]
  (let [[all-expanded-cols all-attrs attr->sort-key sorted-all-cols attr->col]
        (hooks/use-memo
         (fn []
           (let [all-expanded (->> (concat available-cols default-selected-cols)
                                   (mapcat expand-col)
                                   (group-by :attr)
                                   vals
                                   (map first)
                                   vec)
                 all-attrs (set (map :attr all-expanded))
                 attr->sort-key (into {} (map (fn [{:keys [attr]}]
                                                [attr (str/lower-case (label attr))])
                                              all-expanded))
                 sorted-all (vec (sort-by (comp attr->sort-key :attr) all-expanded))
                 attr->col (into {} (map (fn [col] [(:attr col) col]) all-expanded))]
             [all-expanded all-attrs attr->sort-key sorted-all attr->col]))
         [available-cols default-selected-cols])
        !available-list-ref (hooks/use-ref nil)
        !selected-list-ref (hooks/use-ref nil)
        !selection (hooks/use-state {:available #{} :selected #{}})
        !anchor (hooks/use-state {:available nil :selected nil})
        !active-pane (hooks/use-state :selected)
        !context-menu (hooks/use-state nil)
        sortable-debug? (boolean (some-> js/window .-ductileSortableDebug))]
    (hooks/use-effect
     (fn []
       #(do (deframe/route-dissoc! ::temp-col-settings)
            (deframe/route-dissoc! ::search)))
     [])

    (let [original-col-settings (from-localstorage (or col-settings default-selected-cols))
          col-settings @(deframe/!route-get ::temp-col-settings original-col-settings)
          {:keys [cols profile-id]} (selected-profile col-settings)
          selected-columns (vec cols)
          selected-attrs (set (map :attr selected-columns))
          search @(deframe/!route-get ::search)
          search-lower (when (seq search) (str/lower-case search))
          available-columns (let [base (remove (comp selected-attrs :attr) sorted-all-cols)]
                              (->> (if search-lower
                                     (filter (fn [{:keys [attr]}]
                                               (str/includes? (attr->sort-key attr) search-lower))
                                             base)
                                     base)
                                   vec))
          pane->items {:available available-columns
                       :selected selected-columns}
          pane-attrs (fn [pane] (mapv :attr (get pane->items pane [])))
          item-index (fn [pane attr]
                       (first (keep-indexed (fn [idx a] (when (= a attr) idx))
                                            (pane-attrs pane))))
          temp-assoc! (fn [new-cols]
                        (let [new-settings (update-profile col-settings profile-id assoc :cols
                                                           (sort-by (complement :sticky?) new-cols))]
                          (deframe/route-assoc! ::temp-col-settings new-settings)))
          set-pane-selection!
          (fn [pane attrs anchor-attr]
            (let [other-pane (if (= pane :available) :selected :available)]
              (reset! !selection {pane (set attrs)
                                  other-pane #{}})
              (reset! !anchor {pane anchor-attr
                               other-pane nil}))
            (reset! !active-pane pane))
          update-selection-from-lasso!
          (fn [pane {:keys [keys append?]}]
            (let [next (if append?
                         (set/union (get @!selection pane #{}) keys)
                         (set keys))
                  ordered (pane-attrs pane)
                  anchor-attr (or (get @!anchor pane)
                                  (first (filter next ordered)))]
              (set-pane-selection! pane next anchor-attr)))
          handle-row-click!
          (fn [pane attr ^js event]
            (let [ordered (pane-attrs pane)
                  idx (item-index pane attr)
                  anchor (or (item-index pane (get @!anchor pane)) idx)
                  selected (get @!selection pane #{})
                  append? (or (.-metaKey event) (.-ctrlKey event))
                  shift? (.-shiftKey event)
                  range-set (if (and (number? idx) (number? anchor))
                              (set (subvec ordered (min idx anchor) (inc (max idx anchor))))
                              #{attr})
                  next-set (cond
                             shift? (if append?
                                      (set/union selected range-set)
                                      range-set)
                             append? (if (contains? selected attr)
                                       (disj selected attr)
                                       (conj selected attr))
                             :else #{attr})
                  next-anchor (if shift?
                                (or (get @!anchor pane) attr)
                                attr)]
              (set-pane-selection! pane next-set next-anchor)
              (reset! !context-menu nil)))
          remove-selected!
          (fn [attrs]
            (let [attrs (set attrs)]
              (when (seq attrs)
                (temp-assoc! (vec (remove #(contains? attrs (:attr %)) selected-columns)))
                (set-pane-selection! :selected (set/difference (get @!selection :selected #{}) attrs) nil))))
          move-selection-edge!
          (fn [edge]
            (let [selection (get @!selection :selected #{})]
              (when (seq selection)
                (temp-assoc! (move-selected-to-edge selected-columns selection edge)))))
          on-sortable-move!
          (fn [{:keys [source-id target-id slot drag-keys]}]
            (let [moving-attrs (vec drag-keys)
                  moving-set (set moving-attrs)]
              (cond
                (and (= source-id :selected) (= target-id :selected))
                (when (seq moving-attrs)
                  (temp-assoc! (move-selected-block selected-columns moving-attrs (or slot 0)))
                  (set-pane-selection! :selected moving-set (first moving-attrs)))

                (and (= source-id :selected) (= target-id :available))
                (when (seq moving-attrs)
                  (remove-selected! moving-attrs)
                  (set-pane-selection! :selected #{} nil)
                  (set-pane-selection! :available moving-set (first moving-attrs)))

                (and (= source-id :available) (= target-id :selected))
                (let [moving-cols (vec (keep attr->col moving-attrs))]
                  (when (seq moving-cols)
                    (let [existing (remove #(contains? moving-set (:attr %)) selected-columns)
                          insert-idx (max 0 (min (or slot (count existing)) (count existing)))
                          next-cols (insert-at (vec existing) insert-idx moving-cols)]
                      (temp-assoc! next-cols)
                      (set-pane-selection! :selected moving-set (first moving-attrs))
                      (set-pane-selection! :available #{} nil))))

                :else nil)))
          row-context-menu!
          (fn [pane attr ^js event]
            (.preventDefault event)
            (.stopPropagation event)
            (let [pane-selection (get @!selection pane #{})]
              (if (contains? pane-selection attr)
                (reset! !active-pane pane)
                (set-pane-selection! pane #{attr} attr)))
            (when (= pane :selected)
              (reset! !context-menu {:x (.-clientX event)
                                     :y (.-clientY event)})))
          available-lasso (rect-selection/use-rect-selection
                           {:container-ref !available-list-ref
                            :item-selector ".ductile-sortable-item[data-col-attr]"
                            :item-key-fn (fn [^js el]
                                           (some-> el (.getAttribute "data-col-attr") reader/read-string))
                            :start? (fn [^js event]
                                      (let [target (.-target event)
                                            attr (some-> (.closest target ".ductile-sortable-item[data-col-attr]")
                                                         (.getAttribute "data-col-attr")
                                                         reader/read-string)]
                                        (and (nil? (.closest target "button,a,input,textarea,select"))
                                             (not (contains? (get @!selection :available #{}) attr)))))
                            :on-preview #(update-selection-from-lasso! :available %)
                            :on-commit #(update-selection-from-lasso! :available %)})
          selected-lasso (rect-selection/use-rect-selection
                          {:container-ref !selected-list-ref
                           :item-selector ".ductile-sortable-item[data-col-attr]"
                           :item-key-fn (fn [^js el]
                                          (some-> el (.getAttribute "data-col-attr") reader/read-string))
                           :start? (fn [^js event]
                                     (let [target (.-target event)
                                           attr (some-> (.closest target ".ductile-sortable-item[data-col-attr]")
                                                        (.getAttribute "data-col-attr")
                                                        reader/read-string)]
                                       (and (nil? (.closest target "button,a,input,textarea,select"))
                                            (not (contains? (get @!selection :selected #{}) attr)))))
                           :on-preview #(update-selection-from-lasso! :selected %)
                           :on-commit #(update-selection-from-lasso! :selected %)})
          changed? (not= col-settings original-col-settings)
          can-save? (seq selected-columns)
          save! (fn []
                  (deframe/route-dissoc! ::temp-col-settings)
                  (on-change col-settings)
                  (on-close))
          available-selection (get @!selection :available #{})
          selected-selection (get @!selection :selected #{})
          available-ordered-attrs (mapv :attr available-columns)
          selected-ordered-attrs (mapv :attr selected-columns)
          selected-cols-by-attr (into {} (map (juxt :attr identity)) selected-columns)
          selected-selection-cols (keep selected-cols-by-attr selected-selection)
          all-selected-sticky? (and (seq selected-selection-cols)
                                    (every? :sticky? selected-selection-cols))
          some-selected-sticky? (boolean (some :sticky? selected-selection-cols))]

      (hooks/use-effect
       (fn []
         (let [on-key-down (fn [^js event]
                             (when (= "Escape" (.-key event))
                               (if @sortable/!active-drag
                                 (do
                                   (.preventDefault event)
                                   (.stopPropagation event)
                                   (sortable/cancel-active-drag!))
                                 (let [any-selection? (or (seq (get @!selection :available))
                                                          (seq (get @!selection :selected)))]
                                   (when any-selection?
                                     (.preventDefault event)
                                     (.stopPropagation event)
                                     (reset! !selection {:available #{} :selected #{}})
                                     (reset! !anchor {:available nil :selected nil})
                                     (reset! !active-pane :selected)
                                     (reset! !context-menu nil))))))]
           (.addEventListener js/window "keydown" on-key-down true)
           #(do (.removeEventListener js/window "keydown" on-key-down true))))
       [!selection])

      [:div.flex.flex-col
       [:div.flex.rounded.border.border-slate-200.flex-1 {:class "gap-[1px]"}
        [:div.flex.flex-col.bg-white.rounded-l {:class "w-1/2 h-[70dvh]" :data-testid "available-columns"}
         [:div.border-b.border-slate-200.px-3.py-2.font-medium.h-10.flex.shrink-0.items-center.justify-start.gap-1.select-none
          {:class "text-[12px]"}
          (tr "Available columns")
          [ui/badge {:size 1 :variant :white-blue} (- (count all-attrs) (count selected-attrs))]]
         [:div.pt-2.px-3
          [ui/input {:left [:div.h-full.items-center.flex.pl-2.rounded-l-sm
                            [ui/icon {:size 18} :magnifying-glass]]
                     :placeholder (str (tr "Search columns") "…")
                     :value search
                     :on-clear (when (seq search)
                                 #(deframe/route-dissoc! ::search))
                     :on {:change [[:store/route-assoc ::search [:event.target/value]]]}}]]
         [:div.flex-grow.py-1.px-2.overflow-y-auto.max-h-full.relative
          {:ref !available-list-ref}
          (cond
            (and (seq search) (empty? available-columns))
            [:div.flex.items-center.justify-center.h-full.select-none
             [:span.opacity-60 {:class "text-[12px]"} (tr "No results")]]
            (empty? available-columns)
            [:div.flex.items-center.justify-center.h-full.select-none
             [:span.opacity-60 {:class "text-[12px]"} (tr "All available columns are currently selected")]])
          [sortable/sortable
           {:items available-columns
            :axis :both
            :indicator-axis :y
            :sortable-id :available
            :sortable-group :columns
            :selected-keys (get @!selection :available #{})
            :key-fn :attr
            :on-move on-sortable-move!
            :can-start-drag? (fn [{:keys [key selected-keys]}]
                               (contains? selected-keys key))
            :resolve-drag-keys (fn [item _]
                                 (let [attr (:attr item)
                                       sel (get @!selection :available #{})
                                       ordered (pane-attrs :available)]
                                   (if (contains? sel attr)
                                     (vec (filter sel ordered))
                                     [attr])))
            :container-class "flex flex-col w-full min-h-full"
            :container-attrs {:on-pointer-down (:on-pointer-down available-lasso)}
            :item-class "mb-0"
            :item-testid-fn (fn [item _] (pr-str (:attr item)))
            :item-attrs (fn [item _]
                          {:data-col-attr (pr-str (:attr item))
                           :on-click (fn [event]
                                       (when-not ((:consume-click? available-lasso))
                                         (handle-row-click! :available (:attr item) event)))
                           :on-double-click (fn [event]
                                              (.stopPropagation event)
                                              (temp-assoc! (vec (concat selected-columns (expand-col (:attr item))))))
                           :on-context-menu (fn [event]
                                              (row-context-menu! :available (:attr item) event))})
            :debug? sortable-debug?
            :render-item (fn [{:keys [attr]} idx]
                           (let [selected? (contains? available-selection attr)
                                 radius-class (if selected?
                                                (selection-shape->radius-class
                                                 (contiguous-selection-shape available-ordered-attrs available-selection attr))
                                                "rounded")]
                             [:div.column-row.group.flex.justify-between.items-center.select-none.leading-4.px-2
                              {:class (str "text-[12px] "
                                           "py-0.5 "
                                           radius-class " "
                                           (if selected?
                                             "bg-amber-100 hover:bg-amber-200 text-slate-800"
                                             (str "hover:bg-indigo-50 text-slate-700 "
                                                  (if (odd? idx) "bg-[#fbfcfd]" "bg-white"))))}
                              [:div.min-w-0.truncate (label attr)]]))}]
          (when-let [{:keys [left top right bottom]} (:overlay-rect available-lasso)]
            (let [^js container @!available-list-ref
                  ^js container-rect (when container (.getBoundingClientRect container))
                  scroll-left (or (some-> container .-scrollLeft) 0)
                  scroll-top (or (some-> container .-scrollTop) 0)
                  local-left (if container-rect
                               (+ (- left (.-left container-rect)) scroll-left)
                               left)
                  local-top (if container-rect
                              (+ (- top (.-top container-rect)) scroll-top)
                              top)]
              [:div.pointer-events-none.absolute.z-20.rounded-sm.border.border-indigo-400
               {:class "bg-indigo-200/20"
                :style {:left local-left
                        :top local-top
                        :width (max 1 (- right left))
                        :height (max 1 (- bottom top))}}]))]]
        [:div.flex.flex-col.gap-2.justify-center.items-center
         [:div.bg-slate-200.h-full {:class "w-[1px]"}]]
        [:div.flex.flex-col.bg-white.rounded-r {:class "w-1/2 h-[70dvh]" :data-testid "selected-columns"}
         [:div.border-b.border-slate-200.px-3.py-2.font-medium.h-10.flex.shrink-0.items-center.justify-start.gap-1.select-none
          {:class "text-[12px]"}
          (tr "Selected columns")
          [ui/badge {:size 1 :variant :white-blue} (count selected-columns)]]
         [:div.pt-2.px-3
          [profile-select {:col-settings col-settings
                           :on-change (fn [new-settings]
                                        (deframe/route-assoc! ::temp-col-settings new-settings))}]]
         [:div.flex-grow.py-1.px-2.overflow-y-auto.max-h-full.relative
          {:ref !selected-list-ref}
          (if (seq selected-columns)
            [sortable/sortable
             {:items selected-columns
              :axis :both
              :indicator-axis :y
              :sortable-id :selected
              :sortable-group :columns
              :selected-keys selected-selection
              :key-fn :attr
              :on-move on-sortable-move!
              :can-start-drag? (fn [{:keys [key selected-keys]}]
                                 (contains? selected-keys key))
              :resolve-drag-keys (fn [item _]
                                   (let [attr (:attr item)
                                         sel selected-selection
                                         ordered (pane-attrs :selected)]
                                     (if (contains? sel attr)
                                       (vec (filter sel ordered))
                                       [attr])))
              :container-class "flex flex-col w-full min-h-full"
              :container-attrs {:on-pointer-down (:on-pointer-down selected-lasso)}
              :item-class "mb-0"
              :item-testid-fn (fn [item _] (pr-str (:attr item)))
              :item-attrs (fn [item _]
                            {:data-col-attr (pr-str (:attr item))
                             :on-click (fn [event]
                                         (when-not ((:consume-click? selected-lasso))
                                           (handle-row-click! :selected (:attr item) event)))
                             :on-double-click (fn [event]
                                                (.stopPropagation event)
                                                (remove-selected! [(:attr item)]))
                             :on-context-menu (fn [event]
                                                (row-context-menu! :selected (:attr item) event))})
              :debug? sortable-debug?
              :render-item (fn [{:keys [attr sticky?]} idx]
                             (let [selected? (contains? selected-selection attr)
                                   radius-class (if selected?
                                                  (selection-shape->radius-class
                                                   (contiguous-selection-shape selected-ordered-attrs selected-selection attr))
                                                  "rounded")]
                               [:div.column-row.group.flex.justify-between.items-center.select-none.leading-4.px-2
                                {:class (str "text-[12px] "
                                             "py-0.5 "
                                             radius-class " "
                                             (if selected?
                                               "bg-amber-100 hover:bg-amber-200 text-slate-800"
                                               (str "hover:bg-indigo-50 text-slate-800 "
                                                    (if (odd? idx) "bg-[#fbfcfd]" "bg-white"))))}
                                [:div.min-w-0.truncate (label attr)]
                                (when sticky?
                                  [ui/icon {:size 13} :drawing-pin-filled])]))}]
            [:div.flex.items-center.justify-center.h-full.select-none
             [:span.opacity-60 {:class "text-[12px]"} (tr "Pick some columns to display")]])
          (when-let [{:keys [left top right bottom]} (:overlay-rect selected-lasso)]
            (let [^js container @!selected-list-ref
                  ^js container-rect (when container (.getBoundingClientRect container))
                  scroll-left (or (some-> container .-scrollLeft) 0)
                  scroll-top (or (some-> container .-scrollTop) 0)
                  local-left (if container-rect
                               (+ (- left (.-left container-rect)) scroll-left)
                               left)
                  local-top (if container-rect
                              (+ (- top (.-top container-rect)) scroll-top)
                              top)]
              [:div.pointer-events-none.absolute.z-20.rounded-sm.border.border-indigo-400
               {:class "bg-indigo-200/20"
                :style {:left local-left
                        :top local-top
                        :width (max 1 (- right left))
                        :height (max 1 (- bottom top))}}]))]]]
       [:div.flex.justify-between.p-3
        [:div (when on-close [ui/button {:variant :white :size 2 :on-click on-close} (tr "Cancel")])]
        [:div [ui/button {:size 2
                          :disabled? (not can-save?)
                          :on-click save!
                          :variant (if changed? :primary :secondary)}
               (tr "Save")]]]
       (when @!context-menu
         [popover/anchored-popover
          {:x (:x @!context-menu)
           :y (:y @!context-menu)
           :class "rounded-lg border border-slate-200 bg-white shadow-[0_12px_28px_rgba(15,23,42,0.18)] min-w-44"
           :on-close #(reset! !context-menu nil)}
          [:div.flex.flex-col.items-stretch.p-1 {:class "text-[12px]"}
           [:button.text-left.px-2.py-1.5.rounded.hover:bg-slate-100
            {:type "button"
             :data-popover-item "true"
             :disabled (empty? selected-selection)
             :class (when (empty? selected-selection) "text-slate-400 cursor-not-allowed")
             :on-click (fn [_]
                         (remove-selected! selected-selection)
                         (reset! !context-menu nil))}
            (tr "Remove")]
           (when enable-sticky-cols?
             [:button.text-left.px-2.py-1.5.rounded.hover:bg-slate-100
              {:type "button"
               :data-popover-item "true"
               :disabled (empty? selected-selection)
               :class (when (empty? selected-selection) "text-slate-400 cursor-not-allowed")
               :on-click (fn [_]
                           (when (seq selected-selection)
                             (let [target-sticky? (not all-selected-sticky?)
                                   sel selected-selection]
                               (temp-assoc! (mapv (fn [col]
                                                    (if (contains? sel (:attr col))
                                                      (assoc col :sticky? target-sticky?)
                                                      col))
                                                  selected-columns))))
                           (reset! !context-menu nil))}
              (tr (if some-selected-sticky? "Unpin selection" "Pin selection"))])
           [:button.text-left.px-2.py-1.5.rounded.hover:bg-slate-100
            {:type "button"
             :data-popover-item "true"
             :disabled (empty? selected-selection)
             :class (when (empty? selected-selection) "text-slate-400 cursor-not-allowed")
             :on-click (fn [_]
                         (move-selection-edge! :top)
                         (reset! !context-menu nil))}
            (tr "Move to top")]
           [:button.text-left.px-2.py-1.5.rounded.hover:bg-slate-100
            {:type "button"
             :data-popover-item "true"
             :disabled (empty? selected-selection)
             :class (when (empty? selected-selection) "text-slate-400 cursor-not-allowed")
             :on-click (fn [_]
                         (move-selection-edge! :bottom)
                         (reset! !context-menu nil))}
            (tr "Move to bottom")]]])])))

(defn select-columns-modal [{:keys [col-settings on-close on-change available-cols default-selected-cols] :as opts}]
  [ui/modal {:on-close on-close
             :close-attrs {:class "p-1"}
             :attrs {:class "w-[900px]"}}
   [select-columns opts]])

(defscene select-columns-scene
  [select-columns {:available-cols [{:attr :transport/origin
                                     #_#_:children [:address/city
                                                    :address/country]}
                                    :fahrzeug/parking-spot]
                   :column-order [:vehicle/vin
                                  :vehicle/make
                                  :vehicle/model
                                  :fahrzeug/priority
                                  :fahrzeug/destination
                                  #_{:attr :fahrzeug/destination
                                     #_#_:children [:address/name
                                                    :address/postcode]}
                                  :vehicle/weight]}])
